import React, { useEffect, useRef, useState } from 'react';
import apiService from '../apiService';
import './landing.css';

/**
 * Page marketing Mbiyo — vitrine publique de mbiyo.app (visiteurs non connectés).
 * Charte stricte (tokens --mb-*) : feu pour agir, Aeonik, gris froids, ombres douces.
 * Motion sobre : révélations au scroll (une fois), press feedback, hover gated.
 */
const SERVICES = [
  { ico: '🚕', t: 'Courses',    d: 'Un chauffeur en quelques secondes, prix affiché avant de confirmer' },
  { ico: '🛵', t: 'Livraison',  d: 'Restaurants, supermarchés et pharmacies livrés chez vous' },
  { ico: '📦', t: 'Colis',      d: "D'un point à l'autre de la ville, suivi et codes sécurisés" },
  { ico: '🎟️', t: 'Billets',    d: 'Concerts et événements — achetez et entrez avec votre code' },
  { ico: '🏨', t: 'Hôtels',     d: 'Réservez une chambre en quelques touches' },
  { ico: '💇', t: 'Beauté',     d: 'Prenez rendez-vous en salon ou à domicile' },
  { ico: '🚗', t: 'Location',   d: 'Louez un véhicule, avec ou sans chauffeur' },
  { ico: '🎉', t: 'Salles',     d: 'Réservez une salle pour vos événements, acompte en ligne' },
];

const fc = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} FC`;

/** Tarifs de base en vigueur (lus en direct sur le serveur, sans connexion) — demande banque 2026-10-24. */
function useTariffs() {
  const [t, setT] = useState(null);
  useEffect(() => {
    apiService.get('/public/tariffs').then((r) => { if (r?.success) setT(r); }).catch(() => {});
  }, []);
  return t;
}

// Révélation au scroll — une seule fois, IntersectionObserver, stagger via delay inline.
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const els = root.querySelectorAll('.ld-reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '-60px 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
}

export default function LandingPage({ onLogin, onRegister, isAuthenticated }) {
  const ref = useReveal();
  const tariffs = useTariffs();

  return (
    <div className="ld" ref={ref}>
      {/* Mention de l'éditeur (société exploitante) */}
      <div className="ld-company">
        Mbiyo est un service de <b>GAME CENTER SASU</b><span className="rccm"> · RCCM CD/BKV/RCCM23-B-00201</span>
      </div>

      {/* Nav */}
      <nav className="ld-nav">
        <div className="wrap">
          <span className="ld-logo"><img className="mark-img" src="/logo192.png" alt="" />Mbiyo</span>
          <span className="spacer" />
          <button className="ld-link" onClick={onLogin}>{isAuthenticated ? "Ouvrir l'app" : "Se connecter"}</button>
          <a className="ld-btn fire sm" href="/telecharger">Télécharger l'app</a>
        </div>
      </nav>

      {/* Hero */}
      <section className="ld-hero">
        <div className="wrap">
          <span className="eyebrow ld-reveal">🇨🇩 La super-app de votre ville</span>
          <h1 className="ld-reveal" style={{ transitionDelay: '60ms' }}>
            Bougez, commandez, réservez.<br />
            <span className="fire">Votre ville vous suit.</span>
          </h1>
          <p className="ld-reveal" style={{ transitionDelay: '120ms' }}>
            Courses en ville, livraison de repas, colis, billets d'événements, hôtels,
            beauté, location de véhicules et salles — tout Mbiyo dans votre poche, et sur le web.
          </p>
          <div className="ctas ld-reveal" style={{ transitionDelay: '180ms' }}>
            <a className="ld-btn fire" href="/telecharger">📲 Télécharger l'application</a>
            <button className="ld-btn ghost" onClick={onRegister}>{isAuthenticated ? "Ouvrir l'app" : "Commander sur le web"}</button>
          </div>
          <div className="stores ld-reveal" style={{ transitionDelay: '240ms' }}>
            Disponible sur <a href="/telecharger">Android — Google Play</a> · <a href="/telecharger">iPhone — App Store</a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="ld-services">
        <div className="wrap">
          <h2 className="ld-reveal">Tout, dans une seule app</h2>
          <p className="sub ld-reveal">Huit services du quotidien, un seul compte, un seul solde Mbiyo.</p>
          <div className="ld-grid">
            {SERVICES.map((s, i) => (
              <div className="ld-service ld-reveal" key={s.t} style={{ transitionDelay: `${Math.min(i * 50, 300)}ms` }}>
                <span className="ico">{s.ico}</span>
                <b>{s.t}</b>
                <span>{s.d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos tarifs — prix visibles sans compte */}
      <section className="ld-how" id="tarifs">
        <div className="wrap">
          <h2 className="ld-reveal">Nos tarifs</h2>
          <p className="sub ld-reveal">Prix en francs congolais (FC). Le prix exact est toujours affiché avant de confirmer.</p>
          <div className="ld-steps">
            <div className="ld-step ld-reveal">
              <b>🚕 Course en voiture</b>
              <p>{tariffs ? `Prise en charge ${fc(tariffs.rides.car.base)}, puis ${fc(tariffs.rides.car.per_km)} par km et ${fc(tariffs.rides.car.per_min)} par minute.` : 'Prise en charge, puis prix au kilomètre et à la minute.'}</p>
            </div>
            <div className="ld-step ld-reveal" style={{ transitionDelay: '60ms' }}>
              <b>🏍️ Course à moto</b>
              <p>{tariffs ? `Prise en charge ${fc(tariffs.rides.moto.base)}, puis ${fc(tariffs.rides.moto.per_km)} par km et ${fc(tariffs.rides.moto.per_min)} par minute.` : 'Prise en charge, puis prix au kilomètre et à la minute.'}</p>
            </div>
            <div className="ld-step ld-reveal" style={{ transitionDelay: '120ms' }}>
              <b>🛵 Livraison et colis</b>
              <p>{tariffs ? `${fc(tariffs.delivery.base)} + ${fc(tariffs.delivery.per_km)} par km, minimum ${fc(tariffs.delivery.min)}. Prix des produits fixés par chaque commerce.` : 'Frais selon la distance, prix des produits fixés par chaque commerce.'}</p>
            </div>
          </div>
          <p className="sub ld-reveal" style={{ marginTop: 18, fontSize: '.85rem' }}>
            {tariffs?.note || 'Tarifs indicatifs ; ils peuvent varier selon la ville, l\'heure et la demande.'}
          </p>
          <div className="ctas ld-reveal" style={{ marginTop: 14 }}>
            <a className="ld-btn fire" href="/marche">🛍️ Voir les boutiques et leurs prix</a>
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="ld-how">
        <div className="wrap">
          <h2 className="ld-reveal">Comment ça marche</h2>
          <p className="sub ld-reveal">Trois étapes, et la ville s'occupe du reste.</p>
          <div className="ld-steps">
            {[
              ['1', 'Créez votre compte', 'En deux minutes, avec votre numéro de téléphone. Gratuit, sans engagement.'],
              ['2', 'Choisissez un service', 'Une course, un repas, un colis, un billet… Le prix est affiché avant de confirmer.'],
              ['3', 'Suivez en temps réel', 'Chauffeur ou livreur sur la carte, notifications à chaque étape, codes de sécurité.'],
            ].map(([n, t, d], i) => (
              <div className="ld-step ld-reveal" key={n} style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="num">{n}</div>
                <b>{t}</b>
                <p>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partenaires */}
      <section className="ld-partners">
        <div className="wrap">
          <h2 className="ld-reveal">Travaillez avec Mbiyo</h2>
          <p className="sub ld-reveal">Chauffeurs, livreurs, commerçants — rejoignez le réseau et développez vos revenus.</p>
          <div className="cards">
            <div className="ld-partner driver ld-reveal">
              <b>🚕 Devenez chauffeur ou livreur</b>
              <p>Courses et livraisons, gains clairs, retraits depuis l'app. Inscription avec véhicule — ou sans, pour livrer.</p>
              <a href="/telecharger/chauffeur">Télécharger Mbiyo Chauffeur</a>
            </div>
            <div className="ld-partner business ld-reveal" style={{ transitionDelay: '80ms' }}>
              <b>🏪 Vendez avec Mbiyo Business</b>
              <p>Restaurant, boutique, hôtel, institut… Créez votre catalogue en quelques minutes — même depuis une photo de votre menu — et soyez payé dès le retrait.</p>
              <a href="/telecharger/vendeur">Télécharger Mbiyo Business</a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="ld-footer">
        <div className="wrap">
          <span className="ld-logo"><img className="mark-img" src="/logo192.png" alt="" />Mbiyo</span>
          <div className="links">
            <button onClick={onLogin}>{isAuthenticated ? "Ouvrir l'app" : "Se connecter"}</button>
            {!isAuthenticated && <button onClick={onRegister}>Créer un compte</button>}
            <a href="/telecharger">Télécharger</a>
            <a href="/marche">Boutiques et prix</a>
            <a href="/a-propos">À propos</a>
            <a href="/privacy">Confidentialité</a>
            <a href="/cgu">CGU</a>
            <a href="/livraison">Livraison</a>
            <a href="/remboursement">Remboursement</a>
          </div>
          <span className="copy">© {new Date().getFullYear()} Mbiyo — GAME CENTER SASU · RCCM CD/BKV/RCCM23-B-00201 — Kinshasa</span>
        </div>
      </footer>
    </div>
  );
}
